import dayjs from "dayjs";

// Type for translation function
type TranslationFunction = (key: string) => string;

/**
 * Calculates the duration between two dates and formats it as a human-readable string
 * @param startDate - The start date as a string
 * @param endDate - The end date as a string (optional if isCurrentPosition is true)
 * @param isCurrentPosition - Whether this is an ongoing/current position
 * @param t - Translation function
 * @param translationPrefix - Prefix for translation keys (default: "common.duration")
 * @returns Formatted duration string
 */
export const calculateDuration = (
	startDate: string | undefined,
	endDate: string | undefined,
	isCurrentPosition: boolean,
	t: TranslationFunction,
	translationPrefix: string = "common.duration"
): string => {
	if (!startDate) return "";

	const start = dayjs(startDate);
	const end = isCurrentPosition ? dayjs() : dayjs(endDate);

	if (!end.isValid()) return "";

	const diffMonths = end.diff(start, "month");
	const years = Math.floor(diffMonths / 12);
	const months = diffMonths % 12;

	let duration = "";
	if (years > 0) {
		const yearKey = `${translationPrefix}.${years > 1 ? "years" : "year"}`;
		const andKey = `${translationPrefix}.and`;
		const monthsKey = `${translationPrefix}.months`;

		duration += `${years} ${t(yearKey)}`;
		if (months > 0) {
			duration += ` ${t(andKey)} ${months} ${t(monthsKey)}`;
		}
	} else if (months > 0) {
		const monthsKey = `${translationPrefix}.months`;
		duration += `${months} ${t(monthsKey)}`;
	}

	return duration;
};

/**
 * Formats a date string for display using dayjs
 * @param dateString - The date string to format
 * @param language - The language locale to use
 * @param format - The format string (default: "MMM YYYY")
 * @param toLowerCase - Whether to convert to lowercase (default: true)
 * @returns Formatted date string
 */
export const formatDateForDisplay = (
	dateString: string | undefined,
	language: string,
	format: string = "MMM YYYY",
	toLowerCase: boolean = true
): string => {
	if (!dateString) return "";
	const date = dayjs(dateString).locale(language);
	if (!date.isValid()) return "";

	const formatted = date.format(format);
	return toLowerCase ? formatted.toLowerCase() : formatted;
};
